import React, { forwardRef } from 'react';
import ReactSelectBase, { components } from 'react-select';
import ReactSelectAsync from 'react-select/async';
import { withAsyncPaginate } from 'react-select-async-paginate';

const CLASS_NAME_PREFIX = 'react-select';
/**
 * The actions that passed null in react-select 3 when they emptied a multi-select. deselect-option is left
 * out on purpose: version 3 already passed [] for it, so adding it would change behaviour on any select
 * with hideSelectedOptions={false} (none today).
 */
const MULTI_CLEAR_ACTIONS = ['remove-value', 'pop-value', 'clear'];

/**
 * Adds the shared class name prefix (styled by ReactSelectGlobalStyle) and keeps react-select 3's
 * onChange contract: removing or clearing the last multi value passes null, not version 5's [],
 * which the existing handlers and formik validations rely on.
 * @param {React.ComponentType} SelectComponent The react-select component to wrap.
 */
function withLegacyBehaviour(SelectComponent) {
    return forwardRef(function LegacyBehaviourSelect({ onChange, ...props }, ref) {
        const handleChange = (value, actionMeta) => {
            const isClearedMulti =
                props.isMulti &&
                Array.isArray(value) &&
                value.length === 0 &&
                MULTI_CLEAR_ACTIONS.includes(actionMeta.action);

            if (onChange) {
                onChange(isClearedMulti ? null : value, actionMeta);
            }
        };

        return (
            <SelectComponent ref={ref} {...props} classNamePrefix={CLASS_NAME_PREFIX} onChange={handleChange} />
        );
    });
}

/**
 * @deprecated Legacy single entry point for react-select (Select, AsyncSelect, AsyncPaginate, components),
 * kept so existing selects look and behave as before. New code uses SelectDropdown, MultiSelectDropdown
 * or SearchableSelectDropdown from components/library.
 * Never import react-select or react-select-async-paginate directly; style parts through the
 * react-select__* classes in ReactSelect.styles.js, never through generated css-<hash> names.
 */
const Select = withLegacyBehaviour(ReactSelectBase);

export const AsyncSelect = withLegacyBehaviour(ReactSelectAsync);

export const AsyncPaginate = withAsyncPaginate(Select);

/**
 * react-select's stock parts (MultiValueLabel, DropdownIndicator ...), re-exported so custom `components={{...}}`
 * overrides can wrap the stock part and keep its styles and classes instead of importing react-select directly.
 * Used by FormFields, PMCGradeBook and ModuleDetail.
 */
export { components };

export default Select;
